import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parseApiUtc } from '@/utils/apiDate';
import { appDateTime, appMonthNameYear } from '@/utils/appDateFormat';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import Select from '../../../../components/ui/Select';
import StatusBadge from '../../../../components/ui/StatusBadge';
import { Spinner } from '../../../../components/ui/Spinner';
import { downloadWpsFile, markFileSubmitted, voidWpsFile } from '../../../../api/Payroll/wps';
import { WpsConfirmWarning, WpsSifSubmissionView, WpsVoidReason } from '../../../../models/payroll';
import { formatNumber } from '../../../../utils';
import { getApiErrorMessage } from '../../../../utils/apiErrors';
import { InlineConfirm } from './InlineConfirm';
import { useWpsConfirm } from './useWpsConfirm';
import { useWpsLabels } from './wpsLabels';

const VOID_REASONS: WpsVoidReason[] = ['NeverSentToBank', 'BankRejectedFile', 'BankReturnedMoney', 'Other'];

const statusBadge: Record<string, 'success' | 'neutral' | 'error'> = {
  Generated: 'neutral',
  Submitted: 'success',
  Voided: 'error',
};

interface FileActionProps {
  file: WpsSifSubmissionView;
  onDone: () => void;
  onCancel: () => void;
}

/** Stops a whole file. Asks for the choice first, then the server names who goes back and what they should check. */
const VoidPanel: React.FC<FileActionProps> = ({ file, onDone, onCancel }) => {
  const { t } = useTranslation();
  const labels = useWpsLabels();
  const [reason, setReason] = useState<WpsVoidReason | ''>('');
  const [note, setNote] = useState('');
  const finished = useCallback(() => onDone(), [onDone]);
  const { warnings, error, busy, run, reset } = useWpsConfirm(finished);

  const noteNeeded = reason === 'Other';
  const ready = reason !== '' && (!noteNeeded || note.trim().length > 0);

  const preview = (nextReason: WpsVoidReason, nextNote: string) => {
    reset();
    if (nextReason === 'Other' && !nextNote.trim()) return;
    void run(() => voidWpsFile(file.id, { reason: nextReason, note: nextReason === 'Other' ? nextNote.trim() : undefined, confirm: false }));
  };

  return (
    <InlineConfirm
      warnings={warnings}
      error={error}
      busy={busy}
      destructive
      confirmLabel={t('payroll.wps.uae.void.confirm')}
      canConfirm={ready && warnings !== null}
      onConfirm={() => {
        if (reason === '') return;
        void run(() => voidWpsFile(file.id, { reason, note: noteNeeded ? note.trim() : undefined, confirm: true }));
      }}
      onCancel={onCancel}
      testId="wps-void-panel"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Select
          label={t('payroll.wps.uae.void.reason')}
          placeholder={t('payroll.wps.uae.hold.reasonPlaceholder')}
          value={reason}
          options={VOID_REASONS.map(value => ({ value, label: labels.voidReason(value) }))}
          onChange={(value: WpsVoidReason) => {
            setReason(value);
            preview(value, note);
          }}
          data-testid="wps-void-reason"
        />
        {noteNeeded && (
          <Input
            label={t('payroll.wps.uae.note')}
            value={note}
            maxLength={500}
            required
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNote(e.target.value)}
            onBlur={() => {
              preview('Other', note);
            }}
            data-testid="wps-void-note"
          />
        )}
      </div>
      <p className="text-xs text-muted-foreground">{t('payroll.wps.uae.takeOut.whichToUse')}</p>
    </InlineConfirm>
  );
};

type RowPanel = 'download' | 'mark' | 'void' | null;

interface FileRowProps {
  file: WpsSifSubmissionView;
  onChanged: () => void;
  onNotify: (message: string) => void;
}

const FileRow: React.FC<FileRowProps> = ({ file, onChanged, onNotify }) => {
  const { t } = useTranslation();
  const labels = useWpsLabels();
  const [panel, setPanel] = useState<RowPanel>(null);

  const markFinished = useCallback(() => {
    setPanel(null);
    onNotify(t('payroll.wps.uae.history.marked'));
    onChanged();
  }, [onChanged, onNotify, t]);
  const mark = useWpsConfirm(markFinished);

  const [downloadWarnings, setDownloadWarnings] = useState<WpsConfirmWarning[] | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const download = async (confirm: boolean) => {
    setDownloading(true);
    setDownloadError(null);
    try {
      const warnings = await downloadWpsFile(file.id, confirm);
      if (warnings === null) {
        setDownloadWarnings(null);
        setPanel(null);
      } else {
        setDownloadWarnings(warnings);
        setPanel('download');
      }
    } catch (e) {
      setDownloadError(getApiErrorMessage(e, t));
      setPanel('download');
    } finally {
      setDownloading(false);
    }
  };

  const isVoided = file.status === 'Voided';
  const statusText = (() => {
    switch (file.status) {
      case 'Generated': return t('payroll.wps.uae.history.statusGenerated');
      case 'Submitted': return t('payroll.wps.uae.status.submitted');
      case 'Voided': return t('payroll.wps.uae.history.statusVoided');
      default: return '';
    }
  })();

  const released = file.releasedPeople ?? [];

  return (
    <li className="p-3" data-testid={`wps-row-${file.id}`} data-status={file.status}>
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0 space-y-0.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-foreground">{appMonthNameYear(new Date(file.year, file.month - 1))}</span>
            <StatusBadge variant={statusBadge[file.status] ?? 'neutral'} size="sm">{statusText}</StatusBadge>
          </div>
          <p className="text-sm text-muted-foreground">
            {t('payroll.wps.uae.history.summary', { count: file.employeeCount, amount: formatNumber(file.totalAmount) })}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('payroll.wps.uae.history.generated', {
              date: appDateTime(parseApiUtc(file.generatedOn)),
              name: file.generatedByName,
            })}
          </p>
          {isVoided && (
            <p className="text-xs text-red-700 dark:text-red-300" data-testid="wps-row-voided">
              {t('payroll.wps.uae.history.voided', {
                name: file.voidedByName ?? '',
                date: file.voidedOn ? appDateTime(parseApiUtc(file.voidedOn)) : '',
                reason: labels.voidReason(file.voidReason),
              })}
              {file.voidNote && <> {file.voidNote}</>}
            </p>
          )}
          {released.map(person => (
            <p key={`${person.employeeName}-${person.releasedOn ?? ''}`} className="text-xs text-muted-foreground" data-testid="wps-row-released">
              {t('payroll.wps.uae.history.released', { name: person.employeeName, reason: labels.lineReason(person.reason) })}
              {person.note && <> {person.note}</>}
            </p>
          ))}
          {file.isLegacy && !isVoided && (
            <p className="text-xs text-muted-foreground">{t('payroll.wps.uae.history.legacy')}</p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-1">
          {file.hasStoredFile && panel !== 'download' && (
            <Button size="sm" variant="ghost" iconName="Download" onClick={() => download(false)} loading={downloading} disabled={downloading} data-testid="wps-file-download">
              {t('payroll.wps.uae.history.download')}
            </Button>
          )}
          {/* Hidden only while its own panel answers. A warning left behind by moving to Void or Download must not lose the action. */}
          {file.status === 'Generated' && (panel !== 'mark' || (mark.warnings === null && mark.error === null)) && (
            <Button
              size="sm"
              variant="outline"
              iconName="Check"
              loading={mark.busy}
              disabled={mark.busy}
              onClick={() => {
                mark.reset();
                void mark.run(() => markFileSubmitted(file.id, false));
                setPanel('mark');
              }}
              data-testid="wps-file-mark"
            >
              {t('payroll.wps.uae.history.markSubmitted')}
            </Button>
          )}
          {!isVoided && panel !== 'void' && (
            <Button size="sm" variant="ghost" onClick={() => setPanel('void')} data-testid="wps-file-void-open">
              {t('payroll.wps.uae.void.action')}
            </Button>
          )}
        </div>
      </div>

      {panel === 'download' && (
        <InlineConfirm
          warnings={downloadWarnings}
          error={downloadError}
          busy={downloading}
          confirmLabel={t('payroll.wps.uae.history.downloadAnyway')}
          canConfirm={downloadWarnings !== null}
          onConfirm={() => void download(true)}
          onCancel={() => {
            setPanel(null);
            setDownloadWarnings(null);
            setDownloadError(null);
          }}
          testId="wps-download-confirm"
        />
      )}
      {panel === 'mark' && (mark.warnings !== null || mark.error !== null) && (
        <InlineConfirm
          warnings={mark.warnings}
          error={mark.error}
          busy={mark.busy}
          confirmLabel={t('payroll.wps.uae.history.markAnyway')}
          canConfirm={mark.warnings !== null}
          onConfirm={() => void mark.run(() => markFileSubmitted(file.id, true))}
          onCancel={() => {
            mark.reset();
            setPanel(null);
          }}
          testId="wps-mark-confirm"
        />
      )}
      {panel === 'void' && (
        <VoidPanel
          file={file}
          onDone={() => {
            setPanel(null);
            onChanged();
          }}
          onCancel={() => setPanel(null)}
        />
      )}
    </li>
  );
};

interface FileHistoryProps {
  files: WpsSifSubmissionView[];
  loading: boolean;
  /** The list did not answer, so an empty one says nothing about whether files exist. */
  loadFailed: boolean;
  onChanged: () => void;
  onNotify: (message: string) => void;
}

/** One row per file, newest first. A list rather than a table, so the inline panels fit a phone without a nested scroller. */
export const FileHistory: React.FC<FileHistoryProps> = ({ files, loading, loadFailed, onChanged, onNotify }) => {
  const { t } = useTranslation();
  const sorted = useMemo(
    () => [...files].sort((a, b) => new Date(b.generatedOn).getTime() - new Date(a.generatedOn).getTime()),
    [files],
  );

  return (
    <section className="rounded-lg border border-border bg-card" data-testid="wps-history-table">
      <h3 className="font-semibold px-3 pt-3">{t('payroll.wps.uae.history.title')}</h3>
      {loading && sorted.length === 0 && (
        <div className="py-8 text-center"><Spinner size="md" /></div>
      )}
      {!loading && !loadFailed && sorted.length === 0 && (
        <p className="p-3 text-sm text-muted-foreground">{t('payroll.wps.uae.history.empty')}</p>
      )}
      <ul className="divide-y divide-border">
        {sorted.map(file => (
          <FileRow key={file.id} file={file} onChanged={onChanged} onNotify={onNotify} />
        ))}
      </ul>
    </section>
  );
};
